import { ApiError } from './api-error';

export abstract class BaseApi {
    constructor(
        private readonly baseUrl: string,
        private readonly tokenProvider: () => Promise<string>,
    ) {}

    protected static getBaseUrl(envVar: string) {
        const env = import.meta.env ?? process.env;

        return env[`VITE_${envVar}`] ?? env[`REACT_APP_${envVar}`] ?? window.location.origin;
    }

    protected buildUrl(path: string): string {
        return this.baseUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
    }

    protected async getHeaders(): Promise<HeadersInit> {
        const token = await this.tokenProvider();
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }

    protected async get(path: string) {
        const url = this.buildUrl(path);
        const response = await fetch(url, {
            method: 'GET',
            headers: await this.getHeaders(),
            credentials: 'omit',
        });

        if (!response.ok) {
            await ApiError.fromResponse(response);
        }

        return response;
    }

    protected async post(path: string, payload: object) {
        const url = this.buildUrl(path);
        const isFormData = payload instanceof FormData;
        const baseHeaders = (await this.getHeaders()) as Record<string, string>;
        const { 'Content-Type': _omit, ...headersWithoutContentType } = baseHeaders;
        const finalHeaders: HeadersInit = isFormData ? headersWithoutContentType : baseHeaders;

        const response = await fetch(url, {
            method: 'POST',
            headers: finalHeaders,
            credentials: 'omit',
            body: isFormData ? payload : JSON.stringify(payload),
        });

        if (!response.ok) {
            await ApiError.fromResponse(response);
        }

        return response;
    }

    protected getDownloadFileName(response: Response): string {
        const disposition = response.headers.get('content-disposition');
        if (disposition && disposition.indexOf('filename=') !== -1) {
            // This regex looks for "filename" followed by an equals sign,
            // then either a quoted or unquoted filename.
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
            if (match && match[1]) {
                let filename = match[1].trim();

                // Remove surrounding quotes if they exist
                if (filename.startsWith('"') && filename.endsWith('"')) {
                    filename = filename.slice(1, -1);
                }
                return filename;
            }
        }
        return 'download.csv';
    }

    protected async saveDownloadedFile(response: Response) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.getDownloadFileName(response);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }
}
